import { type BookDetail, type BookPatch, buildPath } from "@contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, upload } from "../../../api/fetcher";
import { queryKeys } from "../../../api/keys";

/** Refresh every view whose metadata, pricing, or download can change with Book edits. */
export function useBookMutation<Input>(
  id: string,
  mutationFn: (input: Input) => Promise<BookDetail>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (book) => client.setQueryData(queryKeys.book(id), book),
    onSettled: () =>
      Promise.all(
        [
          queryKeys.books,
          queryKeys.estimate(id),
          queryKeys.casting(id),
          queryKeys.renderProgress(id),
          queryKeys.audiobook(id),
        ].map((queryKey) => client.invalidateQueries({ queryKey })),
      ),
  });
}

export function usePatchBook(id: string) {
  return useBookMutation(id, (patch: BookPatch) =>
    api<BookDetail>(buildPath("/books/:id", { id }), { method: "PATCH", body: patch }),
  );
}

export function useCoverChange(id: string) {
  return useBookMutation(id, (file: File | null) => {
    const path = buildPath("/books/:id/cover", { id });
    if (file === null) return api<BookDetail>(path, { method: "DELETE" });
    const body = new FormData();
    body.append("file", file);
    return upload<BookDetail>(path, body);
  });
}
